/* Additional airport guides for CCU, AMD, PNQ, JAI, GOX, LKO (added Feb 2026).
   Merged into AIRPORT_GUIDES in airportGuides.js. Hand-authored with official
   source links. Zone pins live alongside DEL/BOM/BLR in LOUNGE_ZONES. */

export const EXTRA_GUIDES = {
  CCU: {
    iata: "CCU",
    name: "Netaji Subhas Chandra Bose International Airport",
    city: "Kolkata",
    state: "West Bengal",
    slug: "kolkata-ccu",
    operator: "Airports Authority of India (AAI)",
    last_verified: "2026-01-15",
    sources: [{ label: "AAI Kolkata", url: "https://www.aai.aero/en/airports/kolkata" }],
    hero: "https://images.unsplash.com/photo-1558431382-27e303142255?auto=format&fit=crop&w=2200&q=80",
    terminals: [
      {
        code: "T2",
        name: "Terminal 2 (Integrated)",
        type: "Integrated — international + domestic",
        airlines: ["Air India", "IndiGo", "Vistara", "SpiceJet", "Emirates", "Qatar Airways", "Singapore Airlines", "Thai AirAsia", "Bhutan Airlines", "Druk Air"],
        description:
          "Single integrated glass-façade terminal, opened 2013. Four-level layout — Level 2 Departures, Level 1 Arrivals, Level M Transfer. Capacity 25 MPPA (expansion to 45 by 2028 under AAI master plan).",
        gates: 46,
        inter_terminal:
          "Everything happens inside T2 — international arrivals walk directly to the domestic transfer corridor on Level M.",
        tips: [
          "The East forecourt is quieter for pickup than the busy West (main) forecourt.",
          "The CCU–Metro Yellow Line extension to the airport is partially operational — Jai Hind station is the airport stop.",
        ],
      },
    ],
    lounges: [
      {
        name: "Travel Club Lounge — International",
        terminal: "T2",
        location: "International Departures, Level 2 (post-security)",
        type: "Pay-in / Priority Pass / DreamFolks",
        hours: "24/7",
        amenities: ["Bengali + continental buffet", "Showers", "Family zone"],
        access_cards: ["Priority Pass", "DreamFolks", "Amex Platinum"],
        tagline: "Reliable international lounge — great Bengali breakfast on morning Thai/Singapore flights.",
      },
      {
        name: "Encalm Lounge — Domestic",
        terminal: "T2",
        location: "Domestic Departures (post-security)",
        type: "Pay-in / DreamFolks",
        hours: "24/7",
        amenities: ["Buffet", "Showers"],
        access_cards: ["DreamFolks", "Most Indian credit cards"],
        tagline: "Clean, modern, decent food — the default domestic lounge.",
      },
      {
        name: "Air India Maharajah Lounge",
        terminal: "T2",
        location: "International Departures (post-security)",
        type: "Airline (Star Alliance)",
        hours: "Open with AI international departures",
        amenities: ["Buffet", "Bar", "Showers"],
        access_cards: ["Air India First/Business", "Star Alliance Gold"],
        tagline: "Quieter alternative on morning AI 302/307 Dubai departures.",
      },
    ],
  },

  AMD: {
    iata: "AMD",
    name: "Sardar Vallabhbhai Patel International Airport",
    city: "Ahmedabad",
    state: "Gujarat",
    slug: "ahmedabad-amd",
    operator: "Adani Airports",
    last_verified: "2026-01-15",
    sources: [{ label: "AMD Airport", url: "https://www.adaniairports.com/CSMIA.aspx" }],
    hero: "https://images.unsplash.com/photo-1609942072337-c3370e0accde?auto=format&fit=crop&w=2200&q=80",
    terminals: [
      {
        code: "T1",
        name: "Terminal 1",
        type: "Domestic",
        airlines: ["IndiGo", "Air India", "SpiceJet", "Akasa Air"],
        description:
          "Original terminal fully refurbished in 2024 under Adani — new food court, 10 self-service kiosks, 4 new boarding gates.",
        gates: 16,
        inter_terminal: "T1 ↔ T2 is a 600 m landside walk or a 2-minute shuttle — allow 15 minutes with security.",
        tips: ["Avoid the kerb-side pickup zone — use the paid short-stay (first 10 minutes free)."],
      },
      {
        code: "T2",
        name: "Terminal 2",
        type: "International",
        airlines: ["Air India", "Emirates", "Lufthansa", "Qatar Airways", "flydubai", "Air Arabia"],
        description:
          "International terminal with 10 aerobridges. Immigration counters expanded in 2025 to 22. Shares landside with T1 but airside is strictly separated.",
        gates: 12,
        inter_terminal: "Through-check: domestic → international same-ticket connections have baggage tagged to final destination.",
        tips: [
          "Minimum connecting time: 2h (domestic → international).",
          "Direct airport–Vastral East metro line scheduled to open late 2026 (Ahmedabad Metro Phase 2).",
        ],
      },
    ],
    lounges: [
      {
        name: "Adani Lounge — International",
        terminal: "T2",
        location: "International Departures (post-security)",
        type: "Pay-in / Priority Pass / DreamFolks",
        hours: "24/7",
        amenities: ["Gujarati thali", "Showers", "Family zone", "Bar"],
        access_cards: ["Priority Pass", "DreamFolks", "Amex Platinum"],
        tagline: "Adani's flagship for AMD — authentic Gujarati thali station.",
      },
      {
        name: "Encalm Lounge — Domestic",
        terminal: "T1",
        location: "Domestic Departures (post-security)",
        type: "Pay-in / DreamFolks",
        hours: "24/7",
        amenities: ["Buffet", "Showers", "Work pods"],
        access_cards: ["DreamFolks", "Most Indian credit cards"],
        tagline: "Clean domestic option — can get busy at the 06:00 Mumbai/Delhi wave.",
      },
      {
        name: "Air India Maharajah Lounge",
        terminal: "T2",
        location: "International Departures",
        type: "Airline (Star Alliance)",
        hours: "Open with AI international departures",
        amenities: ["Buffet", "Bar", "Showers"],
        access_cards: ["Air India First/Business", "Star Alliance Gold"],
        tagline: "Quietest option at AMD T2.",
      },
    ],
  },

  PNQ: {
    iata: "PNQ",
    name: "Pune International Airport",
    city: "Pune",
    state: "Maharashtra",
    slug: "pune-pnq",
    operator: "Airports Authority of India (AAI, civil enclave)",
    last_verified: "2026-01-15",
    sources: [{ label: "AAI Pune", url: "https://www.aai.aero/en/airports/pune" }],
    hero: "https://images.unsplash.com/photo-1549221987-25a490f65d34?auto=format&fit=crop&w=2200&q=80",
    terminals: [
      {
        code: "T1",
        name: "Terminal 1 (Integrated · New Terminal)",
        type: "Integrated — international + domestic",
        airlines: ["IndiGo", "Air India", "Vistara", "SpiceJet", "Akasa Air", "Lufthansa", "Oman Air", "Thai AirAsia"],
        description:
          "New integrated terminal opened Sept 2024, replacing the small AAI civil enclave. 3-level layout, 34 check-in counters, 10 aerobridges. Capacity 19 MPPA.",
        gates: 10,
        inter_terminal: "Single terminal — all airlines in one building.",
        tips: [
          "PNQ is a civil enclave inside IAF Lohegaon — follow airport road signage, not generic 'airport' pins on map apps.",
          "The upcoming Purandar greenfield airport (NMA) will take over long-haul in 2028+; until then PNQ is the only Pune option.",
        ],
      },
    ],
    lounges: [
      {
        name: "Encalm Lounge — Integrated",
        terminal: "T1",
        location: "Post-security (first floor), serves both domestic + international",
        type: "Pay-in / Priority Pass / DreamFolks",
        hours: "24/7",
        amenities: ["Buffet (Maharashtrian + continental)", "Showers", "Work pods"],
        access_cards: ["Priority Pass", "DreamFolks", "Amex Platinum"],
        tagline: "The only lounge in the new terminal — tends to fill up during the 18:00–21:00 Delhi/Mumbai wave.",
      },
      {
        name: "Plaza Premium (Observation)",
        terminal: "T1",
        location: "Mezzanine (post-security)",
        type: "Pay-in / Priority Pass / DreamFolks",
        hours: "05:00–23:30",
        amenities: ["Light meals", "Showers"],
        access_cards: ["Priority Pass", "DreamFolks"],
        tagline: "Smaller, quieter alternative with a tarmac view — great for a 45-min connection.",
      },
    ],
  },

  JAI: {
    iata: "JAI",
    name: "Jaipur International Airport",
    city: "Jaipur",
    state: "Rajasthan",
    slug: "jaipur-jai",
    operator: "Adani Airports",
    last_verified: "2026-01-15",
    sources: [{ label: "Jaipur Airport (Adani)", url: "https://www.adaniairports.com/Jaipur.aspx" }],
    hero: "https://images.unsplash.com/photo-1599661046289-e31897846e41?auto=format&fit=crop&w=2200&q=80",
    terminals: [
      {
        code: "T1",
        name: "Terminal 1",
        type: "Domestic only",
        airlines: ["IndiGo (overflow)", "Alliance Air"],
        description:
          "Older AAI-era terminal, now used mostly for pilgrim charter and overflow domestic operations after T2's expansion.",
        gates: 6,
        inter_terminal: "T1 ↔ T2 is a 400 m landside walk or shared shuttle — allow 15 minutes.",
        tips: ["Confirm your terminal — the vast majority of IndiGo and Air India flights now operate from T2."],
      },
      {
        code: "T2",
        name: "Terminal 2",
        type: "Integrated — international + domestic",
        airlines: ["IndiGo", "Air India", "Vistara", "SpiceJet", "Akasa Air", "Oman Air", "Air Arabia", "flydubai", "Thai AirAsia"],
        description:
          "Primary terminal with pink-sandstone Rajasthan-inspired façade. 4-level layout, 36 check-in counters, 8 aerobridges. Capacity 7 MPPA (Phase 3 expansion to 15 MPPA under way).",
        gates: 8,
        inter_terminal: "Through-check for same-ticket domestic → international transfers.",
        tips: ["Minimum connecting time: 1h30 (domestic → international)."],
      },
    ],
    lounges: [
      {
        name: "Adani Lounge",
        terminal: "T2",
        location: "Integrated Departures (post-security)",
        type: "Pay-in / Priority Pass / DreamFolks",
        hours: "24/7",
        amenities: ["Rajasthani thali", "Showers", "Work pods"],
        access_cards: ["Priority Pass", "DreamFolks", "Amex Platinum"],
        tagline: "Pink-sandstone wall, authentic Rajasthani breakfast — a signature Jaipur experience.",
      },
      {
        name: "Plaza Premium — Domestic",
        terminal: "T2",
        location: "Domestic Departures (post-security)",
        type: "Pay-in / Priority Pass / DreamFolks",
        hours: "24/7",
        amenities: ["Buffet", "Showers"],
        access_cards: ["Priority Pass", "DreamFolks"],
        tagline: "Reliable second option — the Adani Lounge gets crowded on morning Delhi/Mumbai departures.",
      },
      {
        name: "Pilgrim Lounge (T1)",
        terminal: "T1",
        location: "T1 (ground floor)",
        type: "Pay-in only (walk-in)",
        hours: "During charter operations",
        amenities: ["Light snacks", "Rest chairs"],
        access_cards: ["Walk-in only"],
        tagline: "Only open for charter/pilgrim operations — nothing to go out of your way for.",
      },
    ],
  },

  GOX: {
    iata: "GOX",
    name: "Manohar International Airport (Mopa)",
    city: "North Goa",
    state: "Goa",
    slug: "mopa-gox",
    operator: "GMR Goa International Airport Ltd.",
    last_verified: "2026-01-15",
    sources: [{ label: "GMR Goa Airport", url: "https://www.gmrgoaairport.com/" }],
    hero: "https://images.unsplash.com/photo-1549557381-31a2976c7f4e?auto=format&fit=crop&w=2200&q=80",
    terminals: [
      {
        code: "T1",
        name: "Terminal 1 (Integrated)",
        type: "Integrated — international + domestic",
        airlines: ["IndiGo", "Air India", "Vistara", "SpiceJet", "Akasa Air", "Lufthansa", "Qatar Airways", "British Airways", "flydubai", "TUI"],
        description:
          "Greenfield terminal opened Jan 2023 — serves North Goa (Pernem). Phase 1 capacity 4.4 MPPA, 100% solar-powered, LEED Gold. Faster drive to Baga/Calangute than the older Dabolim airport in South Goa.",
        gates: 11,
        inter_terminal: "Single integrated terminal.",
        tips: [
          "GOX is in North Goa — if your resort is in Palolem/Agonda (South Goa), consider GOI (Dabolim) instead for a shorter drive.",
          "Approved taxi fare board at the ground-level pre-paid taxi counter — avoid freelance drivers.",
        ],
      },
    ],
    lounges: [
      {
        name: "Encalm Prive Lounge",
        terminal: "T1",
        location: "Integrated Departures (post-security)",
        type: "Pay-in / Priority Pass / DreamFolks",
        hours: "24/7",
        amenities: ["Goan + continental buffet", "Full bar", "Showers", "Nap pods"],
        access_cards: ["Priority Pass", "DreamFolks", "Amex Platinum", "HDFC Infinia"],
        tagline: "Stylish Prive lounge with a dedicated Goan corner — one of the best airport lounges in India.",
      },
      {
        name: "GMR Business Lounge",
        terminal: "T1",
        location: "Integrated Departures (post-security)",
        type: "Pay-in / DreamFolks",
        hours: "24/7",
        amenities: ["Buffet", "Showers", "Work pods"],
        access_cards: ["DreamFolks", "Most Indian credit cards"],
        tagline: "Smaller, quieter sibling — great for a 60-min layover.",
      },
    ],
  },

  LKO: {
    iata: "LKO",
    name: "Chaudhary Charan Singh International Airport",
    city: "Lucknow",
    state: "Uttar Pradesh",
    slug: "lucknow-lko",
    operator: "Adani Airports",
    last_verified: "2026-01-15",
    sources: [{ label: "Lucknow Airport (Adani)", url: "https://www.adaniairports.com/Lucknow.aspx" }],
    hero: "https://images.unsplash.com/photo-1587474260584-136574528ed5?auto=format&fit=crop&w=2200&q=80",
    terminals: [
      {
        code: "T1",
        name: "Terminal 1",
        type: "Domestic",
        airlines: ["IndiGo (overflow)", "Alliance Air"],
        description:
          "Older AAI terminal, now used for overflow domestic traffic and smaller regional ATR operations.",
        gates: 6,
        inter_terminal: "T1 ↔ T3 is a 500 m shuttle — allow 15 minutes.",
        tips: ["Confirm your terminal — IndiGo and Air India moved most ops to T3 after the 2024 expansion."],
      },
      {
        code: "T3",
        name: "Terminal 3 (Integrated)",
        type: "Integrated — international + domestic",
        airlines: ["Air India", "IndiGo", "Vistara", "Akasa Air", "SpiceJet", "flydubai", "Saudia", "Oman Air", "Air Arabia"],
        description:
          "Integrated terminal opened Feb 2024 under Adani's modernisation programme — nawabi-themed glass + chikankari embroidery motifs. 76 check-in counters, 7 aerobridges. Capacity 11 MPPA.",
        gates: 10,
        inter_terminal: "Single building — domestic to international through-check available for same-ticket connections.",
        tips: [
          "LKO's Haj terminal (seasonal) operates separately — follow the dedicated Haj pilgrim signage.",
          "Lucknow Metro Red Line now connects the airport to Hazratganj in 25 minutes.",
        ],
      },
    ],
    lounges: [
      {
        name: "Adani Lounge",
        terminal: "T3",
        location: "Integrated Departures (post-security)",
        type: "Pay-in / Priority Pass / DreamFolks",
        hours: "24/7",
        amenities: ["Awadhi buffet (biryani, kebabs)", "Showers", "Work pods"],
        access_cards: ["Priority Pass", "DreamFolks", "Amex Platinum"],
        tagline: "Signature Awadhi menu — the galawati kebabs are the only lounge food worth planning a layover for.",
      },
      {
        name: "Encalm Lounge — Domestic (T3)",
        terminal: "T3",
        location: "Domestic Departures (post-security)",
        type: "Pay-in / DreamFolks",
        hours: "24/7",
        amenities: ["Buffet", "Showers"],
        access_cards: ["DreamFolks", "Most Indian credit cards"],
        tagline: "Reliable domestic option opposite Gate 7.",
      },
      {
        name: "Haj Pilgrim Lounge (seasonal)",
        terminal: "Haj",
        location: "Dedicated Haj terminal (seasonal operations only)",
        type: "Government operated",
        hours: "During Haj & Umrah season",
        amenities: ["Prayer room", "Rest chairs", "Medical help desk"],
        access_cards: ["Haj Committee of India pilgrims only"],
        tagline: "Open only during the Haj & Umrah season — managed by the Haj Committee.",
      },
    ],
  },
};

/* Zone pins for the new airports — added to LOUNGE_ZONES in the main file. */
export const EXTRA_LOUNGE_ZONES = {
  "CCU|T2|Travel Club Lounge — International": { zone: "International Pier", x: 65, y: 32 },
  "CCU|T2|Encalm Lounge — Domestic": { zone: "Domestic Pier", x: 35, y: 55 },
  "CCU|T2|Air India Maharajah Lounge": { zone: "International Pier", x: 65, y: 52 },

  "AMD|T1|Encalm Lounge — Domestic": { zone: "Domestic", x: 50, y: 50 },
  "AMD|T2|Adani Lounge — International": { zone: "International Departures", x: 50, y: 36 },
  "AMD|T2|Air India Maharajah Lounge": { zone: "International Departures", x: 72, y: 55 },

  "PNQ|T1|Encalm Lounge — Integrated": { zone: "Central (post-security)", x: 42, y: 48 },
  "PNQ|T1|Plaza Premium (Observation)": { zone: "Mezzanine", x: 60, y: 40 },

  "JAI|T2|Adani Lounge": { zone: "Integrated Departures", x: 50, y: 38 },
  "JAI|T2|Plaza Premium — Domestic": { zone: "Domestic Departures", x: 30, y: 58 },
  "JAI|T1|Pilgrim Lounge (T1)": { zone: "Charter area", x: 50, y: 50 },

  "GOX|T1|Encalm Prive Lounge": { zone: "Integrated Departures", x: 45, y: 42 },
  "GOX|T1|GMR Business Lounge": { zone: "Integrated Departures", x: 65, y: 55 },

  "LKO|T3|Adani Lounge": { zone: "Integrated Departures", x: 50, y: 38 },
  "LKO|T3|Encalm Lounge — Domestic (T3)": { zone: "Domestic Departures", x: 30, y: 60 },
  "LKO|Haj|Haj Pilgrim Lounge (seasonal)": { zone: "Haj terminal", x: 50, y: 50 },
};
